import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import LogoPolderzicht from '../assets/logo_polderzicht_kantoorlaag.png';
import '../css/ProjectPlanning.css';

const API_URL = '';

const uiText = {
  nl: {
    step1: 'Stap 1: Beschikbare tijdsloten - selecteer er een',
    step2: 'Stap 2: Uw gegevens',
    alreadyBooked: 'Al ingeschreven',
    full: 'Vol - Wachtlijst mogelijk',
    spotsAvailable: (n, max) => `${n} van ${max} ${n === 1 ? 'plek' : 'plekken'} beschikbaar`,
    onWaitlist: (n) => `${n} op wachtlijst`,
    houseNumber: 'Huisnummer',
    houseNumberPlaceholder: 'Bijv. 42',
    name: 'Naam',
    namePlaceholder: 'Uw volledige naam',
    email: 'E-mailadres',
    emailPlaceholder: 'uw@email.nl',
    phone: 'Telefoonnummer',
    phonePlaceholder: '06-12345678',
    selected: 'Geselecteerd',
    waitlistLabel: '(wachtlijst)',
    submitting: 'Bezig met inschrijven...',
    joinWaitlist: 'Plaats mij op de wachtlijst',
    register: 'Inschrijven',
    yourBookings: 'Uw huidige inschrijvingen',
    waitlist: 'Wachtlijst',
    footer: 'Heeft u vragen? Neem contact op via',
    loading: 'Laden...',
    notFound: 'Project niet gevonden',
    errorLoading: 'Kan project niet laden. Probeer het later opnieuw.',
    errorSelect: 'Selecteer een tijdslot',
    errorGeneral: 'Er is een fout opgetreden. Probeer het opnieuw.',
  },
  en: {
    step1: 'Step 1: Available time slots - select one',
    step2: 'Step 2: Your details',
    alreadyBooked: 'Already registered',
    full: 'Full - Waiting list available',
    spotsAvailable: (n, max) => `${n} of ${max} ${n === 1 ? 'spot' : 'spots'} available`,
    onWaitlist: (n) => `${n} on waiting list`,
    houseNumber: 'House number',
    houseNumberPlaceholder: 'E.g. 42',
    name: 'Name',
    namePlaceholder: 'Your full name',
    email: 'Email address',
    emailPlaceholder: 'your@email.com',
    phone: 'Phone number',
    phonePlaceholder: '06-12345678',
    selected: 'Selected',
    waitlistLabel: '(waiting list)',
    submitting: 'Registering...',
    joinWaitlist: 'Join the waiting list',
    register: 'Register',
    yourBookings: 'Your current registrations',
    waitlist: 'Waiting list',
    footer: 'Questions? Contact us at',
    loading: 'Loading...',
    notFound: 'Project not found',
    errorLoading: 'Unable to load project. Please try again later.',
    errorSelect: 'Please select a time slot',
    errorGeneral: 'An error occurred. Please try again.',
  }
};

const ProjectPlanning = () => {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [existingBookings, setExistingBookings] = useState([]);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [lang, setLang] = useState(localStorage.getItem('language') || 'nl');
  const [form, setForm] = useState({ naam: '', email: '', telefoon: '', huisnummer: '' });

  const t = uiText[lang];

  useEffect(() => { localStorage.setItem('language', lang); }, [lang]);
  useEffect(() => { fetchProject(); }, [slug]);
  useEffect(() => {
    if (form.huisnummer && form.huisnummer.length >= 1 && project) {
      checkExisting(form.huisnummer);
    } else {
      setExistingBookings([]);
    }
  }, [form.huisnummer, project]);

  const fetchProject = async () => {
    try {
      const res = await fetch(`${API_URL}/api/planning/${slug}`);
      if (res.status === 404) { setNotFound(true); return; }
      const data = await res.json();
      setProject(data);
    } catch (err) {
      setError(t.errorLoading);
    } finally {
      setLoading(false);
    }
  };

  const checkExisting = async (huisnummer) => {
    try {
      const res = await fetch(`${API_URL}/api/planning/${slug}/check/${huisnummer}`);
      const data = await res.json();
      setExistingBookings(data);
    } catch (err) { /* silent */ }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedSlot) { setError(t.errorSelect); return; }

    setSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      const res = await fetch(`${API_URL}/api/planning/${slug}/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, slotId: selectedSlot })
      });
      const data = await res.json();
      if (!res.ok) { setError(data.message); return; }

      setSuccess(data.message);
      setSelectedSlot(null);
      fetchProject();
      checkExisting(form.huisnummer);
    } catch (err) {
      setError(t.errorGeneral);
    } finally {
      setSubmitting(false);
    }
  };

  const isAlreadyBooked = (slotId) => existingBookings.some(b => b.slotId === slotId);

  if (loading) {
    return <div className="project-planning-page"><div className="project-planning-container"><p>{t.loading}</p></div></div>;
  }

  if (notFound) {
    return <div className="project-planning-page"><div className="project-planning-container"><h1>{t.notFound}</h1></div></div>;
  }

  if (!project) return null;

  return (
    <div className="project-planning-page">
      <div className="project-planning-container">
        <div className="lang-toggle">
          <button className={`lang-btn ${lang === 'nl' ? 'active' : ''}`} onClick={() => setLang('nl')}>NL</button>
          <button className={`lang-btn ${lang === 'en' ? 'active' : ''}`} onClick={() => setLang('en')}>EN</button>
        </div>

        <div className="project-planning-header">
          <img src={LogoPolderzicht} alt="VVE Polderzicht" className="project-planning-logo" />
          <h1>{project.titel[lang]}</h1>
        </div>

        <div className="project-planning-info">
          <p style={{ whiteSpace: 'pre-line' }}>{project.beschrijving[lang]}</p>
        </div>

        <div className="project-planning-slots">
          <h2>{t.step1}</h2>
          <div className="slots-grid">
            {project.slots.map(slot => {
              const booked = isAlreadyBooked(slot.slotId);
              return (
                <div
                  key={slot.slotId}
                  className={`slot-card ${selectedSlot === slot.slotId ? 'selected' : ''} ${booked ? 'already-booked' : ''} ${slot.vol ? 'vol' : ''}`}
                  onClick={() => { if (!booked) { setSelectedSlot(selectedSlot === slot.slotId ? null : slot.slotId); setError(null); } }}
                >
                  <div className="slot-day">{slot.label[lang]}</div>
                  <div className="slot-time">{slot.tijd}</div>
                  <div className="slot-availability">
                    {booked ? (
                      <span className="badge badge-booked">{t.alreadyBooked}</span>
                    ) : slot.vol ? (
                      <span className="badge badge-waitlist">{t.full}</span>
                    ) : (
                      <span className="badge badge-available">{t.spotsAvailable(slot.beschikbaar, slot.maxPlekken)}</span>
                    )}
                  </div>
                  {slot.wachtlijst > 0 && <div className="slot-waitlist">{t.onWaitlist(slot.wachtlijst)}</div>}
                </div>
              );
            })}
          </div>
        </div>

        <div className="project-planning-form-section">
          <h2>{t.step2}</h2>

          {success && <div className="project-planning-alert success">{success}</div>}
          {error && <div className="project-planning-alert error">{error}</div>}

          <form onSubmit={handleSubmit} className="project-planning-form">
            <div className="form-group">
              <label htmlFor="huisnummer">{t.houseNumber} *</label>
              <input type="text" id="huisnummer" value={form.huisnummer} onChange={(e) => setForm({ ...form, huisnummer: e.target.value })} placeholder={t.houseNumberPlaceholder} required />
            </div>
            <div className="form-group">
              <label htmlFor="naam">{t.name} *</label>
              <input type="text" id="naam" value={form.naam} onChange={(e) => setForm({ ...form, naam: e.target.value })} placeholder={t.namePlaceholder} required />
            </div>
            <div className="form-group">
              <label htmlFor="email">{t.email} *</label>
              <input type="email" id="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={t.emailPlaceholder} required />
            </div>
            <div className="form-group">
              <label htmlFor="telefoon">{t.phone}</label>
              <input type="tel" id="telefoon" value={form.telefoon} onChange={(e) => setForm({ ...form, telefoon: e.target.value })} placeholder={t.phonePlaceholder} />
            </div>

            {selectedSlot && (
              <div className="selected-slot-info">
                {t.selected}: <strong>{project.slots.find(s => s.slotId === selectedSlot)?.label[lang]}</strong>
                {project.slots.find(s => s.slotId === selectedSlot)?.vol && <span className="waitlist-notice"> {t.waitlistLabel}</span>}
              </div>
            )}

            <button type="submit" className="project-planning-submit" disabled={submitting || !selectedSlot}>
              {submitting ? t.submitting : selectedSlot && project.slots.find(s => s.slotId === selectedSlot)?.vol ? t.joinWaitlist : t.register}
            </button>
          </form>
        </div>

        {existingBookings.length > 0 && (
          <div className="existing-bookings">
            <h3>{t.yourBookings}</h3>
            {existingBookings.map(b => (
              <div key={b._id} className={`existing-booking ${b.isWachtlijst ? 'waitlist' : ''}`}>
                <span>{project.slots.find(s => s.slotId === b.slotId)?.label[lang] || b.slotId}</span>
                {b.isWachtlijst && <span className="badge badge-waitlist">{t.waitlist}</span>}
              </div>
            ))}
          </div>
        )}

        <div className="project-planning-footer">
          <p>{t.footer} <a href="mailto:bestuurpolderzicht@gmail.com">bestuurpolderzicht@gmail.com</a></p>
        </div>
      </div>
    </div>
  );
};

export default ProjectPlanning;
